import Delete from "@mui/icons-material/Delete";
import ModeEdit from "@mui/icons-material/ModeEdit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { EpisodicEventItemFragment, WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { ReactNode } from "react";

import { formatContainerDate } from "../Containers/containerDisplay";
import {
  containerCountLabel,
  daysUsedInWindow,
  EVENT_TYPE_LABELS,
  eventStatusChip,
  EVENT_WINDOW_DAYS,
  episodicReliefAdvisory,
  finalizeBlockedReason,
  formatEventWindow,
  isEventWindowLapsed,
  lapsedEventAlertCopy,
  unshippedEventWaste,
} from "./episodicDisplay";
import { EventContainersSection } from "./EventContainersSection";
import { ObligationsSection } from "./ObligationsSection";

const EVENT_BASIS_CITATION = "40 CFR 262 subpart L";

function EventFieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

/** The days-used-of-60 progress bar, with the ship-by deadline right-aligned
 * under it. Turns red once the window has lapsed. */
function WindowProgress({ event, lapsed }: { event: EpisodicEventItemFragment; lapsed: boolean }) {
  const daysUsed = daysUsedInWindow(event.startDate);
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={(daysUsed / EVENT_WINDOW_DAYS) * 100}
        color={lapsed ? "error" : "primary"}
        sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
      />
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {daysUsed} of {EVENT_WINDOW_DAYS} days used
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ship &amp; remove waste by {formatContainerDate(event.accumulationDeadline)}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * The lapsed-window alert is `error`. The relief advisory and the
 * zero-container nudge stay `info`: neither is a non-compliance verdict.
 */
function EventStatusAlerts({
  event,
  isOpen,
  lapsed,
}: {
  event: EpisodicEventItemFragment;
  isOpen: boolean;
  lapsed: boolean;
}) {
  const reliefAdvisory = episodicReliefAdvisory(event.relief);
  return (
    <>
      {reliefAdvisory && <Alert severity="info">{reliefAdvisory.body}</Alert>}
      {lapsed && (
        <Alert severity="error">
          {lapsedEventAlertCopy(event.accumulationDeadline, unshippedEventWaste(event.containers))}
        </Alert>
      )}
      {isOpen && event.containers.length === 0 && (
        <Alert severity="info">No containers are attached to this event.</Alert>
      )}
    </>
  );
}

/** Title, status/type/container-count chips, and the action row. */
function EventCardHeader({
  event,
  isActive,
  isOpen,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  isActive: boolean;
  isOpen: boolean;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  const statusChip = eventStatusChip(event);
  const finalizeBlocked = finalizeBlockedReason(unshippedEventWaste(event.containers));
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1.5}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {event.reason}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" color={statusChip.color} label={statusChip.label} />
          <Chip size="small" label={EVENT_TYPE_LABELS[event.type]} />
          <Chip size="small" label={containerCountLabel(event.containerHistory.length)} />
        </Stack>
      </Box>
      <Stack direction="row" spacing={1}>
        {isActive && (
          <Button size="small" startIcon={<ModeEdit />} onClick={onEdit} disabled={!onEdit}>
            Edit
          </Button>
        )}
        {isOpen && (
          // A disabled button fires no hover events, so the tooltip wraps a span.
          <Tooltip title={finalizeBlocked ?? ""} disableHoverListener={finalizeBlocked === null}>
            <span>
              <Button
                size="small"
                onClick={onFinalize}
                disabled={!onFinalize || finalizeBlocked !== null}
              >
                Finalize event
              </Button>
            </span>
          </Tooltip>
        )}
        {!isOpen && (
          <Button size="small" onClick={onReopen} disabled={!onReopen}>
            Reopen event
          </Button>
        )}
        <Button
          size="small"
          color="error"
          startIcon={<Delete />}
          onClick={onDelete}
          disabled={!onDelete}
        >
          Delete event
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * Detail card for one episodic event of any status. The obligations section
 * stays for every status: an unmet obligation is still owed after the window
 * ends.
 */
export function EpisodicEventCard({
  event,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const isOpen = event.status !== WasteEpisodicEventStatus.Finalized;
  const lapsed = isOpen && isEventWindowLapsed(event.accumulationDeadline);

  return (
    // Matches the container detail page's SectionCard chrome: a flat white
    // tile sitting on the page's canvas background.
    <Card elevation={0}>
      <CardContent sx={{ p: 3 }}>
        {/* Roomier than the default form rhythm: these cards are the whole
            page, so the sections can afford to breathe. */}
        <Stack spacing={3.5}>
          <EventCardHeader
            event={event}
            isActive={isActive}
            isOpen={isOpen}
            onEdit={onEdit}
            onFinalize={onFinalize}
            onReopen={onReopen}
            onDelete={onDelete}
          />

          <Grid container spacing={2} rowSpacing={2.5}>
            <EventFieldValue label="Event window">
              {formatEventWindow(event.startDate, event.endDate)}
            </EventFieldValue>
            <EventFieldValue label="Removal deadline">
              {formatContainerDate(event.accumulationDeadline)}
            </EventFieldValue>
            {/* Attached waste stays exempt for as long as the event is open,
                lapsed or not; only finalizing ends it. */}
            {isOpen && (
              <EventFieldValue label="On-site threshold">
                {lapsed
                  ? `Episodic waste exempt · overdue since ${formatContainerDate(event.accumulationDeadline)}`
                  : "Episodic waste exempt · status unchanged"}
              </EventFieldValue>
            )}
            <EventFieldValue label="Basis">{EVENT_BASIS_CITATION}</EventFieldValue>
          </Grid>

          {isOpen && <WindowProgress event={event} lapsed={lapsed} />}

          <EventStatusAlerts event={event} isOpen={isOpen} lapsed={lapsed} />

          <ObligationsSection
            event={event}
            tenantId={tenantId ?? ""}
            stepperEnabled={stepperEnabled}
          />

          <EventContainersSection event={event} />
        </Stack>
      </CardContent>
    </Card>
  );
}
